// A15 — Commission report. Same TVP2 §9 styling as the other admin
// pages: bordered Reporting filter card → bordered Paper card with the
// results table. Engineer is a debounced-search Autocomplete; Month is a
// MUI <input type="month">; report runs automatically when both are set.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Autocomplete,
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { formatHKD } from "@findgeeks/shared";
import { apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";

interface EngineerOpt {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}
interface ListAppUsersRes {
  items: EngineerOpt[];
}

interface CommissionRow {
  job_id: string;
  title: string;
  date: string;
  commission: number;
  status: string;
  price_missing: boolean;
  address: string;
}

interface CommissionResp {
  items: CommissionRow[];
  total: number;
}

const STATUS_LABEL: Record<string, string> = {
  completed: "Complete",
  cancelled: "Cancelled",
  on_site: "On site",
  travelling: "Travelling",
  accepted: "Accepted",
  published: "Published",
  draft: "Draft",
};

export function CommissionReport() {
  const [engineerId, setEngineerId] = useState<string>("");
  const [engineerLabel, setEngineerLabel] = useState<EngineerOpt | null>(null);
  const [month, setMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  // Debounced engineer search.
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  // Load a default page of engineers up-front so the dropdown isn't empty
  // before the operator types anything. Refetches as the search input
  // changes (the debounced value is part of the queryKey).
  const search = useQuery<ListAppUsersRes>({
    queryKey: ["app-users", "engineer-search", debounced],
    queryFn: () =>
      apiFetch<ListAppUsersRes>(
        `/v1/admin/app-users?role=engineer&limit=20&q=${encodeURIComponent(debounced)}`,
      ),
  });

  const report = useQuery<CommissionResp>({
    queryKey: ["admin", "reports", "commission", engineerId, month],
    queryFn: () =>
      apiFetch<CommissionResp>(
        `/v1/admin/reports/commission?engineerId=${engineerId}&month=${month}`,
      ),
    enabled: Boolean(engineerId) && Boolean(month),
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const items = report.data?.items ?? [];
  const start = paginationModel.page * paginationModel.pageSize;
  const slice = items.slice(start, start + paginationModel.pageSize);

  const columns: DataTableColumn<CommissionRow>[] = [
    { field: "date", headerName: "Date" },
    {
      field: "commission",
      headerName: "Commission",
      render: (r) => formatHKD(r.commission),
    },
    {
      field: "title",
      headerName: "Job Title",
      render: (r) => r.title || "-",
    },
    {
      field: "status",
      headerName: "Status",
      render: (r) => STATUS_LABEL[r.status] ?? r.status,
    },
  ];

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 2,
  } as const;

  const labelOf = (o: EngineerOpt) =>
    [o.first_name, o.last_name].filter(Boolean).join(" ") ||
    o.display_name ||
    o.email ||
    o.id;

  return (
    <Box sx={{ p: 3 }}>
      {/* Reporting filter card */}
      <Paper elevation={0} sx={{ ...cardSx, mb: 3, p: 3 }}>
        <Typography sx={sectionHeading}>Reporting</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
          }}
        >
          <Autocomplete<EngineerOpt>
            options={search.data?.items ?? []}
            value={engineerLabel}
            filterOptions={(o) => o}
            loading={search.isFetching}
            getOptionLabel={labelOf}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onInputChange={(_, v) => setInput(v)}
            onChange={(_, v) => {
              setEngineerLabel(v);
              setEngineerId(v?.id ?? "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Choose Engineer"
                helperText="Type to search engineers by name"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <TextField
            variant="standard"
            label="Month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {/* Results card */}
      <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 3, pb: 2 }}
        >
          <Typography
            sx={{
              fontSize: tokens.font.sizes.body,
              color: tokens.colors.admin.textBody,
            }}
          >
            {engineerLabel
              ? `${labelOf(engineerLabel)} — ${month}`
              : "Pick an engineer to run the report"}
          </Typography>
          {report.data && (
            <Typography sx={{ fontSize: tokens.font.sizes.body }}>
              Total: <strong>{formatHKD(report.data.total)}</strong>
            </Typography>
          )}
        </Stack>
        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={slice}
            rowCount={items.length}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowId={(r) => r.job_id}
            loading={report.isFetching}
            bordered={false}
            emptyMessage={
              !engineerId
                ? "No engineer selected"
                : "No completed jobs in this period"
            }
          />
        </Box>
      </Paper>
    </Box>
  );
}
